import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

import { projectsApi, blocksApi, tasksApi, assignmentsApi } from "@/lib/api";
import { confirm } from "@/lib/dialog";
import { fmtTime, addMinutes, isoNoMs } from "@/lib/format";

const EMPTY = {
  rooms: [],
  crew_members: [],
  tasks: [],
  schedule_blocks: [],
  assignments: [],
  blockers: [],
  equipment: [],
  equipment_allocations: [],
  dependencies: [],
};

const LS_PROJECT_KEY = "ps.currentProjectId";

/**
 * Owns the projects list + currently-selected project + its full payload.
 * Schedule.jsx is a thin layout layer on top.
 */
export function useScheduleData() {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [project, setProject] = useState(null);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // Tracks the latest known `updated_at` for any task/block currently being
  // edited. Lets rapid successive edits (e.g., spamming the -15 stepper) chain
  // properly instead of all sending the same stale If-Match header.
  const latestUpdatedAtRef = useRef({ tasks: {}, blocks: {} });
  // Per-id mutation queue. Ensures consecutive PATCHes to the same task/block
  // run strictly serially so each one can use the previous response's
  // updated_at as its If-Match. Prevents 409s on rapid clicks.
  const mutationQueueRef = useRef({ tasks: {}, blocks: {} });

  const enqueueMutation = useCallback((kind, id, op) => {
    const queue = mutationQueueRef.current[kind];
    const prev = queue[id] || Promise.resolve();
    const next = prev.catch(() => {}).then(() => op());
    queue[id] = next;
    // Clean up reference once settled so the object doesn't grow unbounded.
    next.finally(() => {
      if (queue[id] === next) delete queue[id];
    });
    return next;
  }, []);

  const loadFull = useCallback(async (pid) => {
    const full = await projectsApi.full(pid);
    setProject(full.project);
    setData({
      rooms: full.rooms || [],
      crew_members: full.crew_members || [],
      tasks: full.tasks || [],
      schedule_blocks: full.schedule_blocks || [],
      assignments: full.assignments || [],
      blockers: full.blockers || [],
      equipment: full.equipment || [],
      equipment_allocations: full.equipment_allocations || [],
      dependencies: full.dependencies || [],
    });
  }, []);

  const loadList = useCallback(async () => {
    const list = await projectsApi.list();
    setProjects(list);
    return list;
  }, []);

  // Bootstrap: load list, pick project (localStorage > first). If the user has
  // no projects yet we leave selectedProjectId=null so the page can render its
  // first-run empty state (with a "create" CTA + an optional Riverside demo).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await loadList();
        if (cancelled) return;
        if (!list.length) {
          setSelectedProjectId(null);
          return;
        }
        const stored = localStorage.getItem(LS_PROJECT_KEY);
        const pid = (stored && list.some((p) => p.id === stored)) ? stored : list[0].id;
        setSelectedProjectId(pid);
        await loadFull(pid);
      } catch (e) {
        console.error("Failed to load project", e);
        toast.error("Failed to load project.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadFull, loadList]);

  // Persist selection so refresh remembers which project the user was on.
  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem(LS_PROJECT_KEY, selectedProjectId);
    }
  }, [selectedProjectId]);

  const selectProject = useCallback(async (pid) => {
    if (!pid || pid === selectedProjectId) return;
    setLoading(true);
    try {
      setSelectedProjectId(pid);
      await loadFull(pid);
    } catch (e) {
      console.error("Failed to switch project", e);
      toast.error("Failed to switch project.");
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, loadFull]);

  const createProject = useCallback(async (payload) => {
    const created = await projectsApi.create(payload);
    await loadList();
    setSelectedProjectId(created.id);
    await loadFull(created.id);
    return created;
  }, [loadFull, loadList]);

  const refresh = useCallback(() => {
    if (selectedProjectId) return loadFull(selectedProjectId);
    return Promise.resolve();
  }, [selectedProjectId, loadFull]);

  // Background polling — quietly refresh every 30s so co-editors' changes
  // appear without manual reload. Pauses when the tab is hidden to save the API.
  useEffect(() => {
    if (!selectedProjectId) return;
    let timer = null;
    const tick = () => {
      if (document.visibilityState === "visible") {
        loadFull(selectedProjectId).catch(() => {});
      }
    };
    timer = setInterval(tick, 30000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [selectedProjectId, loadFull]);

  const refreshProjectMeta = useCallback(async () => {
    // After project settings save, refresh both the list (for switcher label) and the full payload.
    await loadList();
    if (selectedProjectId) await loadFull(selectedProjectId);
  }, [selectedProjectId, loadFull, loadList]);

  // ── Mutators (optimistic with concurrent-edit guard) ──────────
  const patchTask = useCallback((taskId, updates) => {
    return enqueueMutation("tasks", taskId, async () => {
      let beforeTask = null;
      setData((d) => {
        beforeTask = d.tasks.find((t) => t.id === taskId);
        return {
          ...d,
          tasks: d.tasks.map((t) => (t.id === taskId ? { ...t, ...updates } : t)),
        };
      });
      const expected = latestUpdatedAtRef.current.tasks[taskId] || beforeTask?.updated_at;
      try {
        const updated = await tasksApi.update(taskId, updates, {
          expected_updated_at: expected,
        });
        if (updated) {
          if (updated.updated_at) latestUpdatedAtRef.current.tasks[taskId] = updated.updated_at;
          setData((d) => ({
            ...d,
            tasks: d.tasks.map((t) => (t.id === taskId ? { ...t, ...updated } : t)),
          }));
        }
      } catch (e) {
        const detail = e.response?.data?.detail;
        const isVersionMismatch = e.response?.status === 409 && typeof detail === "object" && detail?.error === "version_mismatch";
        if (isVersionMismatch) {
          toast.warning(detail.message || "Someone else edited this task. Reloading…", { duration: 4000 });
          delete latestUpdatedAtRef.current.tasks[taskId];
        } else {
          const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to update task.");
          toast.error(msg);
        }
        await refresh();
      }
    });
  }, [refresh, enqueueMutation]);

  const patchBlock = useCallback((blockId, updates) => {
    return enqueueMutation("blocks", blockId, async () => {
      let beforeBlock = null;
      setData((d) => {
        beforeBlock = d.schedule_blocks.find((b) => b.id === blockId);
        return {
          ...d,
          schedule_blocks: d.schedule_blocks.map((b) =>
            b.id === blockId ? { ...b, ...updates } : b
          ),
        };
      });
      const expected = latestUpdatedAtRef.current.blocks[blockId] || beforeBlock?.updated_at;
      try {
        const updated = await blocksApi.update(blockId, updates, {
          expected_updated_at: expected,
        });
        if (updated) {
          if (updated.updated_at) latestUpdatedAtRef.current.blocks[blockId] = updated.updated_at;
          setData((d) => ({
            ...d,
            schedule_blocks: d.schedule_blocks.map((b) =>
              b.id === blockId ? { ...b, ...updated } : b
            ),
          }));
        }
      } catch (e) {
        const detail = e.response?.data?.detail;
        // The ONLY 409 we treat as version_mismatch is the structured shape
        // {error: "version_mismatch", ...} returned by the optimistic-concurrency
        // guard. Other 409s (server-side validation, lock-required, etc.) carry
        // their own human-readable detail string and should surface that.
        const isVersionMismatch = e.response?.status === 409 && typeof detail === "object" && detail?.error === "version_mismatch";
        if (isVersionMismatch) {
          toast.warning(detail.message || "Someone else edited this block. Reloading…", { duration: 4000 });
          delete latestUpdatedAtRef.current.blocks[blockId];
          await refresh();
        } else {
          const msg = typeof detail === "string" ? detail : (detail?.message || "Update failed.");
          toast.error(msg);
          // Still refresh so the UI re-syncs to the server's actual state
          // (otherwise the optimistic change lingers visually).
          await refresh();
        }
      }
    });
  }, [refresh, enqueueMutation]);

  const bulkLock = useCallback(async (selection, lock = true) => {
    if (!selection.length) return;
    try {
      await blocksApi.bulkUpdate(selection, { is_locked: lock });
      setData((d) => ({
        ...d,
        schedule_blocks: d.schedule_blocks.map((b) =>
          selection.includes(b.id) ? { ...b, is_locked: lock } : b
        ),
      }));
      toast.success(`${lock ? "Locked" : "Unlocked"} ${selection.length} row${selection.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed.");
    }
  }, []);

  const bulkDelete = useCallback(async (selection) => {
    if (!selection.length) return false;
    const ok = await confirm({
      title: `Delete ${selection.length} scheduled block${selection.length === 1 ? "" : "s"}?`,
      body: "The underlying tasks stay — only the scheduled blocks are removed.",
      confirmLabel: "DELETE",
      danger: true,
    });
    if (!ok) return false;
    try {
      await Promise.all(selection.map((id) => blocksApi.remove(id)));
      setData((d) => ({
        ...d,
        schedule_blocks: d.schedule_blocks.filter((b) => !selection.includes(b.id)),
      }));
      toast.success("Deleted.");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed.");
      return false;
    }
  }, []);

  const scheduleFloating = useCallback(async (taskId, anchorBlock) => {
    if (!project) return;
    const task = data.tasks.find((t) => t.id === taskId);
    if (!task) return;
    const start = anchorBlock.end_at;
    const end = isoNoMs(addMinutes(start, task.duration_min || 60));
    try {
      await blocksApi.create(project.id, {
        task_id: taskId,
        room_id: anchorBlock.room_id,
        start_at: start,
        end_at: end,
        is_locked: false,
      });
      await loadFull(project.id);
      toast.success(`Placed "${task.title}" at ${fmtTime(start)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to place task.");
    }
  }, [project, data.tasks, loadFull]);

  // Place a floating task at the start of a specific day. Uses the day's
  // configured `onsite_time` (project.settings.days[dateKey].onsite_time) when
  // set, otherwise falls back to 08:00. The block lands in the project's first
  // room — the user can drag it to another room afterwards.
  const scheduleFloatingAtDay = useCallback(async (taskId, dateKey) => {
    if (!project || !dateKey) return;
    const task = data.tasks.find((t) => t.id === taskId);
    if (!task) return;
    const daySettings = project.settings?.days?.[dateKey] || {};
    const hhmm = daySettings.onsite_time || "08:00";
    const start = `${dateKey}T${hhmm}:00Z`;
    const end = isoNoMs(addMinutes(start, task.duration_min || 60));
    const roomId = task.primary_room_id || data.rooms[0]?.id;
    if (!roomId) {
      toast.error("Project has no rooms yet — add one before placing tasks.");
      return;
    }
    try {
      await blocksApi.create(project.id, {
        task_id: taskId,
        room_id: roomId,
        start_at: start,
        end_at: end,
        is_locked: false,
      });
      await loadFull(project.id);
      toast.success(`Placed "${task.title}" at ${hhmm}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to place task.");
    }
  }, [project, data.tasks, data.rooms, loadFull]);

  // Place a task at a specific hour-of-day. Used by the Plan view's time-slot
  // drop zones. dateKey "YYYY-MM-DD", hhmm "HH:MM" (24h).
  const scheduleFloatingAt = useCallback(async (taskId, dateKey, hhmm) => {
    if (!project || !dateKey || !hhmm) return;
    const task = data.tasks.find((t) => t.id === taskId);
    if (!task) return;
    const start = `${dateKey}T${hhmm}:00Z`;
    const end = isoNoMs(addMinutes(start, task.duration_min || 60));
    const roomId = task.primary_room_id || data.rooms[0]?.id;
    if (!roomId) {
      toast.error("Project has no rooms yet — add one before placing tasks.");
      return;
    }
    try {
      await blocksApi.create(project.id, {
        task_id: taskId,
        room_id: roomId,
        start_at: start,
        end_at: end,
        is_locked: false,
      });
      await loadFull(project.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to place "${task.title}".`);
    }
  }, [project, data.tasks, data.rooms, loadFull]);

  // Plan a task on a day without committing to a clock time. Sets
  // task.planned_date (YYYY-MM-DD). No schedule_block is created.
  // Pass dateKey=null to clear (un-plan).
  const planTaskOnDay = useCallback(async (taskId, dateKey) => {
    const value = dateKey || ""; // "" → backend clears planned_date
    try {
      await tasksApi.update(taskId, { planned_date: value });
      setData((d) => ({
        ...d,
        tasks: d.tasks.map((t) => (t.id === taskId ? { ...t, planned_date: dateKey || null } : t)),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to plan task.");
    }
  }, []);

  const addAssignment = useCallback(async (blockId, crewMemberId, opts = {}) => {
    try {
      const a = await assignmentsApi.create(blockId, {
        crew_member_id: crewMemberId,
        is_lead: !!opts.is_lead,
        role_on_block: opts.role_on_block || null,
      });
      setData((d) => ({ ...d, assignments: [...d.assignments.filter((x) => x.id !== a.id), a] }));
      return a;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign crew.");
      return null;
    }
  }, []);

  // Task-scoped (floating) assignment — used in ROOMS view on unscheduled
  // tasks. When the task is later scheduled, the backend migrates these
  // onto the new block (schedule_block_id is set, task_id is cleared).
  const addTaskAssignment = useCallback(async (taskId, crewMemberId, opts = {}) => {
    try {
      const a = await assignmentsApi.createForTask(taskId, {
        crew_member_id: crewMemberId,
        is_lead: !!opts.is_lead,
        role_on_block: opts.role_on_block || null,
      });
      setData((d) => ({ ...d, assignments: [...d.assignments.filter((x) => x.id !== a.id), a] }));
      return a;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign crew.");
      return null;
    }
  }, []);

  const removeAssignment = useCallback(async (assignmentId) => {
    try {
      await assignmentsApi.remove(assignmentId);
      setData((d) => ({ ...d, assignments: d.assignments.filter((a) => a.id !== assignmentId) }));
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unassign crew.");
      return false;
    }
  }, []);

  return {
    projects,
    project,
    data,
    loading,
    selectProject,
    createProject,
    refresh,
    refreshProjectMeta,
    patchTask,
    patchBlock,
    bulkLock,
    bulkDelete,
    scheduleFloating,
    scheduleFloatingAtDay,
    scheduleFloatingAt,
    planTaskOnDay,
    addAssignment,
    addTaskAssignment,
    removeAssignment,
  };
}
